import React, { useCallback, useMemo, useState } from 'react';
import { Card, CardBody } from '@spark/ui-components/components/Card';
import { Button } from '@spark/ui-components/components/Button';
import ProgramacaoHavanDateFilter from './ProgramacaoHavanDateFilter';
import ProgramacaoHavanHeader from './ProgramacaoHavanHeader';
import ProgramacaoHavanTable from './ProgramacaoHavanTable';
import ProgramacaoHavanImportModal from './ProgramacaoHavanImportModal';
import ProgramacaoHavanVxModal from './ProgramacaoHavanVxModal';
import ProgramacaoHavanVxResultModal from './ProgramacaoHavanVxResultModal';
import ProgramacaoHavanVxServiceUnavailableModal from './ProgramacaoHavanVxServiceUnavailableModal';
import ProgramacaoHavanVxPesquisaModal from './ProgramacaoHavanVxPesquisaModal';
import ProgramacaoHavanVxAlteracaoModal from './ProgramacaoHavanVxAlteracaoModal';
import ProgramacaoHavanMotoristaForaTmsModal from './ProgramacaoHavanMotoristaForaTmsModal';
import ProgramacaoHavanVeiculoTmsModal from './ProgramacaoHavanVeiculoTmsModal';
import ProgramacaoHavanFiltrosBar from './ProgramacaoHavanFiltrosBar';
import ProgramacaoHavanColunasModal from './ProgramacaoHavanColunasModal';
import ProgramacaoHavanAuditoriaModal from './ProgramacaoHavanAuditoriaModal';
import ProgramacaoHavanCargaManualModal from './ProgramacaoHavanCargaManualModal';
import ProgramacaoHavanLegendaModal from './ProgramacaoHavanLegendaModal';
import { notification } from '@spark/ui-components/utils/notification';
import { getProgramacaoHavanColumns } from './tanStackColumns';
import { carregarLayoutColunas, comLarguras, layoutPersonalizado, salvarLayoutColunas, semLarguras } from '../utils/layoutColunas';
import { exportarProgramacaoDoDia } from '../utils/exportarExcel';
import { useProgramacaoHavanListagem } from '../hooks/useProgramacaoHavanListagem';

const ProgramacaoHavanCard = () => {
  const {
    dateOptions,
    selectedDate,
    selectedDateOption,
    turnoAtivo,
    turnos,
    importModal,
    inlineEditConfig,
    vxConfig,
    vxModal,
    vxLoading,
    vxResult,
    vxUnavailable,
    vxPesquisa,
    vxOpcoesCarga,
    vxConfirmacaoAlteracao,
    motoristaForaTms,
    onFecharMotoristaForaTms,
    onSalvarMotoristaForaTms,
    veiculoTms,
    cadastroTms,
    onFecharVeiculoTms,
    onSalvarVeiculoTms,
    onCancelarAlteracaoComVx,
    onConfirmarAlteracaoComVx,
    totalRegistros,
    onSelectDate,
    onPreviousDate,
    onNextDate,
    onTurnoFilter,
    onChangePage,
    onRetryTurno,
    onRecarregarDataSelecionada,
    onOpenImportModal,
    onCloseImportModal,
    onImportarArquivo,
    onPrevisualizarDefinitiva,
    onAplicarDefinitiva,
    onCloseVxModal,
    onSubmitVx,
    onCloseVxResult,
    onCloseVxUnavailable,
    onRetryVx,
    onCloseVxPesquisa,
    onSubmitVxPesquisa,
    onBaixarLaudoVx,
    vxBaixandoLaudoId
  } = useProgramacaoHavanListagem();

  // Filtros valem só enquanto a tela está aberta; ordem, visibilidade e largura das colunas ficam salvas neste navegador.
  const [filtros, setFiltros] = useState([]);
  const [layoutColunas, setLayoutColunas] = useState(carregarLayoutColunas);
  const [versaoLarguras, setVersaoLarguras] = useState(0);
  const [colunasAberto, setColunasAberto] = useState(false);
  const [auditoriaAberta, setAuditoriaAberta] = useState(false);
  const [cargaManualAberta, setCargaManualAberta] = useState(false);
  const [legendaAberta, setLegendaAberta] = useState(false);
  const cargaIncluida = resultado => {
    setCargaManualAberta(false);
    notification.success(`Carga ${resultado?.numero_carga || ''} incluída na programação.`);
    onRecarregarDataSelecionada();
  };
  // Excel do dia inteiro (sem os filtros da tela), uma aba por turno, com as colunas visíveis na ordem da tela.
  const baixarExcel = () => {
    if (turnos.primeiro.carregando || turnos.segundo.carregando) {
      notification.warning('Aguarde a programação terminar de carregar para baixar o Excel.');
      return;
    }
    try {
      exportarProgramacaoDoDia({ dataProgramacao: selectedDate, turnos, layoutColunas });
    } catch (error) {
      notification.error('Não foi possível gerar o Excel do dia.');
    }
  };
  const colunasPadrao = useMemo(() => getProgramacaoHavanColumns().map(coluna => ({ id: coluna.id, header: coluna.header })), []);
  const salvarColunas = layout => {
    // Restaurar padrão no modal limpa as larguras: as tabelas recomeçam para descartar as que estavam aplicadas.
    const limpouLarguras = Object.keys(layoutColunas.larguras || {}).length > 0 && !Object.keys(layout.larguras || {}).length;
    salvarLayoutColunas(layout);
    setLayoutColunas(layout);
    if (limpouLarguras) setVersaoLarguras(versao => versao + 1);
    setColunasAberto(false);
  };
  // Ao soltar a borda do cabeçalho: a largura nova entra por cima das salvas e vale para os dois turnos;
  // duplo clique na borda tira a coluna das salvas (volta ao tamanho padrão).
  const alterarLarguras = useCallback(({ alteradas = {}, resetadas = [] } = {}) => {
    setLayoutColunas(atual => {
      const proximo = semLarguras(comLarguras(atual, alteradas), resetadas);
      salvarLayoutColunas(proximo);
      return proximo;
    });
  }, []);

  const renderTurno = (chaveTurno, titulo) => {
    const turno = turnos[chaveTurno];

    return (
      <ProgramacaoHavanTable
        id={`programacao-havan-${chaveTurno}`}
        titulo={`${selectedDateOption.diaSemana} (${titulo})`}
        chaveDados={selectedDate || ''}
        data={turno.registros}
        carregando={turno.carregando}
        erro={turno.erro}
        pagina={turno.pagina}
        totalPaginas={turno.totalPaginas}
        totalRegistros={turno.totalRegistros}
        onChangePage={pagina => onChangePage(chaveTurno, pagina)}
        onRetry={() => onRetryTurno(chaveTurno)}
        inlineEditConfig={inlineEditConfig}
        vxConfig={vxConfig}
        filtros={filtros}
        layoutColunas={layoutColunas}
        onAlterarLarguras={alterarLarguras}
        versaoLarguras={versaoLarguras}
      />
    );
  };

  return (
    <Card>
      <CardBody>
        <ProgramacaoHavanHeader
          totalRegistros={totalRegistros}
          onMostrarAuditoriaGeral={() => setAuditoriaAberta(true)}
          actions={<Button type="button" variant="primary" onClick={onOpenImportModal}>Importar programação</Button>}
        />
        <ProgramacaoHavanDateFilter
          dateOptions={dateOptions}
          selectedDate={selectedDate}
          turnoAtivo={turnoAtivo}
          onSelectDate={onSelectDate}
          onPreviousDate={onPreviousDate}
          onNextDate={onNextDate}
          onTurnoFilter={onTurnoFilter}
          onAdicionarCarga={() => setCargaManualAberta(true)}
          onAbrirLegenda={() => setLegendaAberta(true)}
          onBaixarExcel={baixarExcel}
        />
        <ProgramacaoHavanFiltrosBar filtros={filtros} onChangeFiltros={setFiltros} onAbrirColunas={() => setColunasAberto(true)} colunasPersonalizadas={layoutPersonalizado(layoutColunas)} />
        <ProgramacaoHavanColunasModal show={colunasAberto} colunas={colunasPadrao} layout={layoutColunas} onClose={() => setColunasAberto(false)} onSalvar={salvarColunas} />
        {turnoAtivo !== 'segundo' ? renderTurno('primeiro', 'PRIMEIRO TURNO') : null}
        {turnoAtivo !== 'primeiro' ? renderTurno('segundo', 'SEGUNDO TURNO') : null}
        <ProgramacaoHavanImportModal
          show={importModal.show}
          onClose={onCloseImportModal}
          onSubmit={onImportarArquivo}
          onPrevisualizarDefinitiva={onPrevisualizarDefinitiva}
          onAplicarDefinitiva={onAplicarDefinitiva}
        />
        <ProgramacaoHavanVxModal show={vxModal.show} row={vxModal.row} reason={vxModal.reason} opcoesCarga={vxOpcoesCarga.data} carregandoOpcoes={vxOpcoesCarga.carregando} erroOpcoes={vxOpcoesCarga.erro} loading={vxLoading} onClose={onCloseVxModal} onSubmit={onSubmitVx} />
        <ProgramacaoHavanVxResultModal show={Boolean(vxResult)} result={vxResult} baixandoLaudo={vxBaixandoLaudoId != null && vxBaixandoLaudoId === vxResult?.row?.id} onBaixarLaudo={onBaixarLaudoVx} onClose={onCloseVxResult} />
        <ProgramacaoHavanVxServiceUnavailableModal show={vxUnavailable.show} row={vxUnavailable.row} message={vxUnavailable.message} loading={vxLoading} onClose={onCloseVxUnavailable} onRetry={onRetryVx} />
        <ProgramacaoHavanVxPesquisaModal show={vxPesquisa.show} row={vxPesquisa.row} data={vxPesquisa.data} opcoesCarga={vxOpcoesCarga.data} carregandoOpcoes={vxOpcoesCarga.carregando} erroOpcoes={vxOpcoesCarga.erro} erro={vxPesquisa.erro} loading={vxLoading} onClose={onCloseVxPesquisa} onSubmit={onSubmitVxPesquisa} />
        <ProgramacaoHavanMotoristaForaTmsModal show={motoristaForaTms.show} modo={motoristaForaTms.modo || 'usar'} cpfInicial={motoristaForaTms.cpf} nomeInicial={motoristaForaTms.nome} telefoneInicial={motoristaForaTms.telefone || ''} cadastroTms={cadastroTms} onClose={onFecharMotoristaForaTms} onSubmit={onSalvarMotoristaForaTms} />
        <ProgramacaoHavanVeiculoTmsModal show={veiculoTms.show} campo={veiculoTms.campo} placa={veiculoTms.placa} cadastroTms={cadastroTms} onClose={onFecharVeiculoTms} onSubmit={onSalvarVeiculoTms} />
        <ProgramacaoHavanCargaManualModal
          show={cargaManualAberta}
          dataProgramacao={selectedDate}
          turnoPadrao={turnoAtivo === 'segundo' ? 2 : 1}
          onClose={() => setCargaManualAberta(false)}
          onIncluida={cargaIncluida}
        />
        <ProgramacaoHavanLegendaModal show={legendaAberta} onClose={() => setLegendaAberta(false)} />
        <ProgramacaoHavanAuditoriaModal show={auditoriaAberta} dataProgramacao={selectedDate} onClose={() => setAuditoriaAberta(false)} />
        <ProgramacaoHavanVxAlteracaoModal show={vxConfirmacaoAlteracao.show} titulo={vxConfirmacaoAlteracao.titulo} mensagem={vxConfirmacaoAlteracao.mensagem} detalhe={vxConfirmacaoAlteracao.detalhe} onCancel={onCancelarAlteracaoComVx} onConfirm={onConfirmarAlteracaoComVx} />
      </CardBody>
    </Card>
  );
};

export default ProgramacaoHavanCard;
